import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    gatewayPermissionList,
    permissionListSelector,
    setActiveGateway,
    useAppDispatch,
    useAppSelector,
    useGatewaysQuery,
    useUserProfileQuery,
} from '../store';
import { useCallback, useEffect, useState } from 'react';
import { immediateToast } from 'utils/toast';
import { SplashScreenLayout } from 'components/layouts/splash-screen.layout';
import { useGatewayPermission } from 'utils/utils';
import { useGateway } from 'hooks';

export const RegisterRouteMiddleware = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { w } = useParams();
    const dispatch = useAppDispatch();

    const currentGateway = useGateway();
    const userPermissions = useAppSelector(permissionListSelector);
    const gatewayPermissions = useAppSelector(gatewayPermissionList);
    const arePermissionsReady: Boolean = Boolean(
        userPermissions && gatewayPermissions,
    );

    const { data: profile, isLoading: isLoadingProfile } =
        useUserProfileQuery(undefined);

    // The gateway list is feature data, not a bootstrap dependency. Once a
    // response is cached it stays usable, so a navigation-triggered refetch
    // must not re-enter the splash state below. Cold-boot deep links still
    // wait for it through the permission/active-gateway readiness clause.
    const { data: gatewayList, isLoading: isLoadingGatewayList } =
        useGatewaysQuery({ page: 1 }, { skip: profile?.status !== 'accept' });

    useEffect(() => {
        if (gatewayList?.items) {
            const activeGateway = gatewayList?.items?.find(
                (item) => item?.website === w,
            );

            activeGateway && dispatch(setActiveGateway(activeGateway));
        }
    }, [gatewayList, w]);

    useEffect(() => {
        !isLoadingProfile &&
            !isLoadingGatewayList &&
            profile &&
            gatewayList &&
            useGatewayPermission(gatewayList?.items, profile, dispatch);
    }, [isLoadingProfile, isLoadingGatewayList, gatewayList, profile]);

    const handleOutUser = useCallback((text: string) => {
        immediateToast('error', {
            message: `حساب شما ${text} شده است`,
            timeout: 3000,
        });

        setTimeout(() => {
            localStorage.removeItem('token');
            window.location.href = '/';
        }, 3000);
    }, []);

    const handleAcceptedUser = useCallback(() => {
        if (pathname.includes('register')) {
            navigate('/');
        }
    }, []);

    const handleRegisterUser = useCallback((path: string) => {
        navigate('/register/' + path || 'profile');
    }, []);

    const handleStep = useCallback((status: string): string => {
        switch (status) {
            case 'registered':
                return 'profile';
            case 'profile':
                return 'bank';
            case 'bank':
            case 'reject':
                return 'documents';
            case 'upload':
                return 'waiting';

            default:
                return 'profile';
        }
    }, []);

    useEffect(() => {
        if (!isLoadingProfile) {
            if (profile?.status) {
                const { status } = profile;

                if (status === 'ban') {
                    handleOutUser('مسدود');
                } else if (status === 'deleted') {
                    handleOutUser('حذف');
                } else if (status === 'accept') {
                    handleAcceptedUser();
                } else {
                    handleRegisterUser(handleStep(status));
                }
            }
        }
    }, [profile?.status, isLoadingProfile]);

    // Bootstrap gate: profile restoration and the permission maps it feeds
    // are the only splash-worthy dependencies. The lifecycle is strictly
    // one-directional — once this condition has been satisfied it never
    // regresses, so feature-level fetching (gateway list refetches with
    // cached data, the cache reset that follows a gateway switch) keeps
    // the previous tree rendered instead of re-entering the splash.
    const isRouteReady =
        !isLoadingProfile &&
        ((arePermissionsReady && (currentGateway || !w)) ||
            profile?.status !== 'accept');

    const [hasBootstrapped, setHasBootstrapped] = useState(false);

    useEffect(() => {
        if (isRouteReady) {
            setHasBootstrapped(true);
        }
    }, [isRouteReady]);

    if (isRouteReady || hasBootstrapped) {
        return <Outlet />;
    }

    return <SplashScreenLayout />;
};
